/**
 * Install-prompt store.
 *
 * `beforeinstallprompt` fires ONCE, early — usually before React has mounted —
 * and the event is only usable while it is alive. Every component that wanted
 * to offer "Install" was adding its own listener in a `useEffect`, which means
 * whichever one mounted after the event had already fired got nothing. In
 * practice that was all of them: the dashboard pill never had a real install
 * button at all, it linked to a QR-code page, and the toast could only ever
 * appear in the first second of the first page load. Dismiss it once and there
 * was no way back into installing short of knowing about Chrome's address-bar
 * icon.
 *
 * So the event is captured here at module load — imported from main.tsx before
 * `createInertiaApp`, the same trick `initTheme()` uses — held, and handed to
 * whoever asks whenever they ask.
 *
 * Deliberately framework-free so the capture does not wait on React.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform = 'desktop' | 'mobile';

export interface InstallState {
  /** The browser has offered us an install prompt we can still fire. */
  canInstall: boolean;
  /** Running in an installed window already — hide every install affordance. */
  installed: boolean;
  /** Shapes the copy: a shop counter PC is not "your phone". */
  platform: InstallPlatform;
  /**
   * iOS/iPadOS, where no browser fires `beforeinstallprompt`. Installing is a
   * manual Share → Add to Home Screen. Needs instructions, not a button.
   *
   * Was called `iosSafari`, which is why `IOSInstallPrompt` shipped gated to
   * Safari and showed a Chrome-on-iOS user nothing at all. The flag is about
   * the operating system; `iosNeedsSafari` is the one about the browser.
   */
  ios: boolean;
  /**
   * On iOS but not in Safari — Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS),
   * Opera (OPiOS). Apple only documents Add to Home Screen from Safari's share
   * sheet, so these users need one extra step ("open this in Safari") before
   * the normal instructions apply. Without this they are the users who report
   * that installing is impossible, because for them the usual steps are.
   */
  iosNeedsSafari: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    // iOS Safari's non-standard flag.
    ('standalone' in window.navigator &&
      (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

function detectPlatform(): InstallPlatform {
  if (typeof window === 'undefined') return 'desktop';
  /*
   * Coarse pointer + small viewport is a far better proxy than sniffing the
   * UA string, which lies on iPads (they report desktop Safari) and on the
   * Android tablets some of these shops use at the counter.
   */
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const narrow = window.matchMedia('(max-width: 900px)').matches;
  return coarse && narrow ? 'mobile' : 'desktop';
}

function detectIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  /*
   * Every browser on iOS is WebKit underneath — Chrome and Firefox there are
   * Safari in a different coat — so none of them fire `beforeinstallprompt`
   * and all of them install via Share → Add to Home Screen. That makes the
   * browser brand irrelevant; only "is this iOS" matters.
   */
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as macOS; a touch-capable "Mac" is an iPad.
    (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

function detectNonSafariIos(): boolean {
  if (typeof window === 'undefined') return false;
  /*
   * Each iOS browser marks itself in the UA with its own token while still
   * claiming Safari at the end of the string — so "is it Safari" can only be
   * answered by ruling the others out, never by looking for "Safari".
   */
  return /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i.test(
    window.navigator.userAgent,
  );
}

if (typeof window !== 'undefined') {
  installed = detectStandalone();

  window.addEventListener('beforeinstallprompt', (e) => {
    /*
     * preventDefault stops Chrome's own mini-infobar so our in-app surfaces
     * are the single place install is offered. The cost is that if we never
     * call prompt(), the user is never asked — which is why the event is kept
     * rather than dropped.
     *
     * This line imposes an invariant on the whole app, and it has been broken
     * twice: EVERY screen that can receive this event must offer install, and
     * no copy may describe the browser UI this suppresses. The event fires on
     * the first page of the first visit — usually the login screen — so
     * `AuthLayout` must read `canInstall`, not merely link to /get-app, and
     * `InstallOnPhonePill` must not exclude a device from the button. There is
     * no browser fallback behind our UI; we removed it here.
     */
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });

  /*
   * Someone can install from Chrome's address bar while the tab stays open, or
   * launch the installed app later in the same session. Either way the
   * display-mode flips under us and every "Install" button on screen is now a
   * lie, so react to it.
   */
  const standaloneQuery = window.matchMedia('(display-mode: standalone)');
  standaloneQuery.addEventListener?.('change', (e) => {
    if (e.matches) {
      installed = true;
      deferred = null;
      emit();
    }
  });

  // Crossing the desktop/mobile breakpoint changes the copy, and nothing else
  // would tell React to re-read the snapshot.
  window
    .matchMedia('(max-width: 900px)')
    .addEventListener?.('change', () => emit());
}

/*
 * The snapshot is memoised and only replaced when a field actually changes.
 * `useSyncExternalStore` compares snapshots by identity, so returning a fresh
 * object on every call trips React's "the result of getSnapshot should be
 * cached" warning and can spin into an infinite re-render.
 *
 * `platform` is read live rather than at module load because a window dragged
 * between a laptop screen and an external monitor, or simply resized, can cross
 * the breakpoint — and the copy should follow.
 */
let snapshot: InstallState | null = null;

export function getInstallState(): InstallState {
  const ios = detectIos();
  const next: InstallState = {
    canInstall: deferred !== null && !installed,
    installed,
    platform: detectPlatform(),
    ios,
    iosNeedsSafari: ios && detectNonSafariIos(),
  };
  if (
    snapshot === null ||
    snapshot.canInstall !== next.canInstall ||
    snapshot.installed !== next.installed ||
    snapshot.platform !== next.platform ||
    snapshot.ios !== next.ios ||
    snapshot.iosNeedsSafari !== next.iosNeedsSafari
  ) {
    snapshot = next;
  }
  return snapshot;
}

export function subscribeInstallState(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Fire the real browser install dialog.
 *
 * Returns the user's choice, or 'unavailable' when there is no live event —
 * callers must handle that rather than assume a button press installs, because
 * the event is single-use: after a successful prompt() Chrome will not give us
 * another one, and a declined prompt may or may not be re-offered.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const event = deferred;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    // Single-use either way; drop it so no surface keeps offering a dead button.
    deferred = null;
    emit();
    return outcome;
  } catch {
    deferred = null;
    emit();
    return 'unavailable';
  }
}
